import React, { createContext, useContext, useEffect, useState } from "react";

const AuthContext = createContext(null);

// Pulls "Authorization" from any of the shapes fetch accepts (Headers, plain
// object, or a Request). Returns true if it starts with "Bearer ".
function requestHadBearer(input, init) {
  try {
    const fromInit = init?.headers;
    if (fromInit instanceof Headers) {
      return (fromInit.get("Authorization") || "").startsWith("Bearer ");
    }
    if (fromInit && typeof fromInit === "object") {
      const v = fromInit.Authorization ?? fromInit.authorization;
      if (typeof v === "string") return v.startsWith("Bearer ");
    }
    if (input instanceof Request) {
      return (input.headers.get("Authorization") || "").startsWith("Bearer ");
    }
  } catch {}
  return false;
}

// BroadcastChannel key — shared across tabs in the same origin.
const LOGOUT_CHANNEL = "phishmatrix_session";

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState({
    token: null,
    user: null,
    loading: true,
  });

  // Listen for logout events broadcast from other tabs.
  useEffect(() => {
    let channel;
    try {
      channel = new BroadcastChannel(LOGOUT_CHANNEL);
      channel.onmessage = (e) => {
        if (e.data === "logout") {
          sessionStorage.removeItem("auth_token");
          sessionStorage.removeItem("auth_user");
          setAuth({ token: null, user: null, loading: false });
        }
      };
    } catch { /* BroadcastChannel not supported (old Safari) — degrade silently */ }
    return () => { try { channel?.close(); } catch {} };
  }, []);

  useEffect(() => {
    const storedToken = sessionStorage.getItem("auth_token");
    const storedUser = sessionStorage.getItem("auth_user");

    if (storedToken && storedUser) {
      setAuth({
        token: storedToken,
        user: JSON.parse(storedUser),
        loading: false,
      });
    } else {
      setAuth((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  // Global 401 interceptor: when an authenticated request comes back as 401,
  // the token has expired or been revoked. Clear auth state so ProtectedRoute
  // sends the user back to /login automatically, and flag the reason so the
  // login screen can surface it.
  useEffect(() => {
    const originalFetch = window.fetch.bind(window);

    const wrapped = async (input, init) => {
      const response = await originalFetch(input, init);
      if (requestHadBearer(input, init)) {
        // Sliding session: swap in the refreshed token whenever the server
        // sends one back so the 2h inactivity clock resets on every request.
        const newToken = response.headers.get("X-New-Token");
        if (newToken) {
          try {
            sessionStorage.setItem("auth_token", newToken);
            setAuth((prev) => (prev.token ? { ...prev, token: newToken } : prev));
          } catch {}
        }
        if (response.status === 401) {
          try {
            sessionStorage.setItem("pm_session_expired", "1");
            sessionStorage.removeItem("auth_token");
            sessionStorage.removeItem("auth_user");
          } catch {}
          setAuth({ token: null, user: null, loading: false });
        }
      }
      return response;
    };

    window.fetch = wrapped;
    return () => {
      // Only restore if we still own the slot — avoids clobbering another
      // wrapper installed after us during HMR.
      if (window.fetch === wrapped) window.fetch = originalFetch;
    };
  }, []);

  const login = (token, user) => {
    try { sessionStorage.removeItem("pm_session_expired"); } catch {}
    sessionStorage.setItem("auth_token", token);
    sessionStorage.setItem("auth_user", JSON.stringify(user));
    setAuth({ token, user, loading: false });
  };

  const logout = async () => {
    const token = sessionStorage.getItem("auth_token");
    // Revoke the token server-side first, then clear local state.
    if (token) {
      try {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* network error — local clear still proceeds */ }
    }
    sessionStorage.removeItem("auth_token");
    sessionStorage.removeItem("auth_user");
    setAuth({ token: null, user: null, loading: false });
    // Signal all other open tabs to log out immediately.
    try {
      const ch = new BroadcastChannel(LOGOUT_CHANNEL);
      ch.postMessage("logout");
      ch.close();
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ ...auth, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
